import { create } from "zustand";

export type ProjectionStatus = "idle" | "loading" | "ready" | "error";

export interface ProjectionStoreState<T, A extends unknown[]> {
  data: T | null;
  status: ProjectionStatus;
  error: string | null;
  lastLoadedAt: number | null;
  lastInvalidatedAt: number | null;
  args: A;
  fetch: (...args: A) => Promise<T>;
  refresh: () => Promise<T>;
  invalidate: () => void;
  setData: (data: T) => void;
  reset: () => void;
}

export function createProjectionStore<T, A extends unknown[]>(
  fetcher: (...args: A) => Promise<T>,
  defaultArgs: A
) {
  return create<ProjectionStoreState<T, A>>((set, get) => ({
    data: null,
    status: "idle",
    error: null,
    lastLoadedAt: null,
    lastInvalidatedAt: null,
    args: defaultArgs,
    fetch: async (...args: A) => {
      set({ status: "loading", error: null, args });
      try {
        const data = await fetcher(...args);
        set({
          data,
          status: "ready",
          error: null,
          lastLoadedAt: Date.now(),
        });
        return data;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to load projection";
        set({ status: "error", error: message });
        throw error;
      }
    },
    refresh: async () => {
      const { args, data } = get();
      // If we already have data, do a quiet background refresh without loading flash
      if (data) {
        try {
          const fresh = await fetcher(...args);
          set({
            data: fresh,
            status: "ready",
            error: null,
            lastLoadedAt: Date.now(),
          });
          return fresh;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Failed to refresh";
          set({ error: message });
          return data;
        }
      }
      const { fetch } = get();
      return fetch(...args);
    },
    invalidate: () =>
      set((state) => ({
        status: state.data ? state.status : "idle",
        lastInvalidatedAt: Date.now(),
      })),
    setData: (data: T) =>
      set({
        data,
        status: "ready",
        error: null,
        lastLoadedAt: Date.now(),
      }),
    reset: () =>
      set({
        data: null,
        status: "idle",
        error: null,
        lastLoadedAt: null,
        lastInvalidatedAt: null,
        args: defaultArgs,
      }),
  }));
}
